import { productsData } from '../data/ProductsData';
import { collection, getDocs, doc, getDoc, setDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const STORAGE_KEY = 'admin_products';
const MODE = (process.env.REACT_APP_API_MODE || 'firebase').toLowerCase();
const PRODUCTS_COLLECTION = collection(db, 'products');

const normalizeLocalProduct = (p) => ({
  id: p.id || `p_${Date.now()}_${Math.random()}`,
  slug: p.slug || p.id || p.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
  name: p.name || 'Untitled',
  title: p.title || p.name || 'Untitled',
  price: p.price || p.newPrice || 0,
  newPrice: p.newPrice || p.price || 0,
  comparePrice: p.comparePrice || p.oldPrice || 0,
  discount: p.discount || 0,
  rating: p.rating || 0,
  description: p.description || '',
  image: Array.isArray(p.images) ? p.images[0] : (p.image || ''),
  images: p.images || (p.image ? [p.image] : []),
  sizes: p.sizes || ['100 ML'],
  reviews: p.reviews || [],
});

const normalizeFirestoreProduct = (docData, id) => ({
  id,
  slug: docData.slug || id,
  name: docData.name || docData.title || 'Untitled',
  title: docData.title || docData.name || 'Untitled',
  price: docData.price || docData.newPrice || 0,
  newPrice: docData.newPrice || docData.price || 0,
  comparePrice: docData.comparePrice || docData.oldPrice || 0,
  discount: docData.discount || 0,
  rating: docData.rating || 0,
  description: docData.description || '',
  image: Array.isArray(docData.images) ? docData.images[0] : (docData.image || ''),
  images: docData.images || (docData.image ? [docData.image] : []),
  sizes: docData.sizes || ['100 ML'],
  reviews: docData.reviews || [],
  createdAt: docData.createdAt || '',
});

function readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  return Object.values(productsData).map(normalizeLocalProduct);
}

function writeLocal(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    // ignore
  }
}

const slugify = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

async function getProductDocBySlug(slug) {
  const productRef = doc(db, 'products', slug);
  const snapshot = await getDoc(productRef);
  if (snapshot.exists()) return { id: snapshot.id, ...snapshot.data() };
  const q = query(PRODUCTS_COLLECTION, where('slug', '==', slug));
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const docSnapshot = querySnapshot.docs[0];
    return { id: docSnapshot.id, ...docSnapshot.data() };
  }
  return null;
}

export async function listProducts() {
  if (MODE === 'firebase') {
    const querySnapshot = await getDocs(PRODUCTS_COLLECTION);
    return querySnapshot.docs.map((docSnap) => normalizeFirestoreProduct(docSnap.data(), docSnap.id));
  }
  return readLocal();
}

export async function getProduct(idOrSlug) {
  if (MODE === 'firebase') {
    const directRef = doc(db, 'products', idOrSlug);
    const directSnapshot = await getDoc(directRef);
    if (directSnapshot.exists()) {
      return normalizeFirestoreProduct(directSnapshot.data(), directSnapshot.id);
    }
    const product = await getProductDocBySlug(idOrSlug);
    return product ? normalizeFirestoreProduct(product, product.id) : null;
  }
  const list = readLocal();
  return list.find((p) => String(p.id) === String(idOrSlug) || String(p.slug) === String(idOrSlug)) || null;
}

export async function createProduct(payload) {
  if (MODE === 'firebase') {
    const slug = payload.slug?.trim() || slugify(payload.name || `product-${Date.now()}`);
    const productRef = doc(db, 'products', slug);
    const newProduct = {
      ...payload,
      slug,
      image: payload.image || (payload.images?.[0] || ''),
      images: payload.images || (payload.image ? [payload.image] : []),
      createdAt: new Date().toISOString(),
    };
    await setDoc(productRef, newProduct);
    return { id: slug, ...newProduct };
  }
  const list = readLocal();
  const item = { ...payload, id: payload.id || `p_${Date.now()}` };
  const next = [item, ...list];
  writeLocal(next);
  return item;
}

export async function updateProduct(payload) {
  if (MODE === 'firebase') {
    const id = payload.id || payload.slug;
    if (!id) throw new Error('Product id is required');
    const productRef = doc(db, 'products', id);
    const updates = {
      ...payload,
    };
    delete updates.id;
    await updateDoc(productRef, updates);
    return { id, ...payload };
  }
  const list = readLocal();
  const next = list.map((p) => (String(p.id) === String(payload.id) ? payload : p));
  writeLocal(next);
  return payload;
}

export async function deleteProduct(id) {
  if (MODE === 'firebase') {
    await deleteDoc(doc(db, 'products', id));
    return { success: true };
  }
  const list = readLocal();
  const next = list.filter((p) => String(p.id) !== String(id));
  writeLocal(next);
  return { success: true };
}

const productsApi = {
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
};

export default productsApi;
